import { ContentfulBlogPostFields, ContentfulEntry, formatDate } from '@devtinker/shared';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getEntries } from '../lib/contentful';

interface CategoryCount {
  name: string;
  count: number;
}

interface ArchiveMonth {
  label: string;
  year: number;
  month: number;
  count: number;
}

const Blog: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<ContentfulEntry<ContentfulBlogPostFields>[]>([]);
  const [allPosts, setAllPosts] = useState<ContentfulEntry<ContentfulBlogPostFields>[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [archives, setArchives] = useState<ArchiveMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const entries = await getEntries('blogPost');
        const sortedEntries = (entries as ContentfulEntry<ContentfulBlogPostFields>[])
          .sort((a, b) => new Date(b.fields.publishDate).getTime() - new Date(a.fields.publishDate).getTime());
        setAllPosts(sortedEntries);
        setPosts(sortedEntries);
        
        // Extract categories from all posts
        const categoryMap = new Map<string, number>();
        sortedEntries.forEach(post => {
          const tags = post.fields.tags || [];
          tags.forEach((tag: string) => {
            categoryMap.set(tag, (categoryMap.get(tag) || 0) + 1);
          });
        });
        
        const extractedCategories = Array.from(categoryMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
        setCategories(extractedCategories);
        
        // Extract archives (month/year) from posts
        const archiveMap = new Map<string, { year: number; month: number; count: number }>();
        sortedEntries.forEach(post => {
          const date = new Date(post.fields.publishDate);
          const year = date.getFullYear();
          const month = date.getMonth();
          const key = `${year}-${month}`;
          
          if (!archiveMap.has(key)) {
            archiveMap.set(key, { year, month, count: 0 });
          }
          archiveMap.get(key)!.count++;
        });
        
        const extractedArchives = Array.from(archiveMap.entries())
          .map(([, data]) => {
            const date = new Date(data.year, data.month);
            const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            return { label, year: data.year, month: data.month, count: data.count };
          })
          .sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            return b.month - a.month;
          });
        setArchives(extractedArchives);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Filter posts when category changes
  useEffect(() => {
    if (selectedCategory) {
      const filtered = allPosts.filter(post => 
        post.fields.tags?.includes(selectedCategory)
      );
      setPosts(filtered);
    } else {
      setPosts(allPosts);
    }
  }, [selectedCategory, allPosts]);

  // Mock data for when Contentful is not configured
  const mockPosts: ContentfulEntry<ContentfulBlogPostFields>[] = [
    {
      sys: { id: '1', type: 'Entry', createdAt: '2025-01-31', updatedAt: '2025-01-31', contentType: { sys: { id: 'blogPost' } } },
      fields: {
        title: 'Decoupling Technical Architecture to Enhance Resiliency',
        slug: 'decoupling-technical-architecture-to-enhance-resiliency',
        excerpt: "Let's explore the importance of decoupling, what to watch out for, metrics that measure its success, and how to communicate its value to stakeholders.",
        description: "Let's explore the importance of decoupling, what to watch out for, metrics that measure its success, and how to communicate its value to stakeholders.",
        publishDate: '2025-01-31',
        author: 'Chris Smith',
        tags: ['Developer Skills', 'Architecture']
      }
    },
    {
      sys: { id: '2', type: 'Entry', createdAt: '2024-05-09', updatedAt: '2024-05-09', contentType: { sys: { id: 'blogPost' } } },
      fields: {
        title: 'GTD: Back to Basics – Part 2',
        slug: 'gtd-back-to-basics-part-2',
        excerpt: 'The goal here is to give inspiration on productivity tactics to leverage and how they can be utilized to improve your process.',
        description: 'The goal here is to give inspiration on productivity tactics to leverage and how they can be utilized to improve your process.',
        publishDate: '2024-05-09',
        author: 'Chris Smith',
        tags: ['Productivity']
      }
    },
    {
      sys: { id: '3', type: 'Entry', createdAt: '2024-02-13', updatedAt: '2024-02-13', contentType: { sys: { id: 'blogPost' } } },
      fields: {
        title: 'Launching DevTinker Experiments',
        slug: 'launching-devtinker-experiments',
        excerpt: 'Finding a place to store and manage some clever tricks and coding implementations has led us to create our Experiments section.',
        description: 'Finding a place to store and manage some clever tricks and coding implementations has led us to create our Experiments section.',
        publishDate: '2024-02-13',
        author: 'Chris Smith',
        tags: ['Coding', 'Projects']
      }
    },
    {
      sys: { id: '4', type: 'Entry', createdAt: '2023-09-07', updatedAt: '2023-09-07', contentType: { sys: { id: 'blogPost' } } },
      fields: {
        title: 'GTD: Back to Basics – Part 1',
        slug: 'gtd-back-to-basics-part-1',
        excerpt: 'There comes a point in that journey, where you need to refresh yourself on the basics, to come back to your foundations.',
        description: 'There comes a point in that journey, where you need to refresh yourself on the basics, to come back to your foundations.',
        publishDate: '2023-09-07',
        author: 'Chris Smith',
        tags: ['Productivity']
      }
    },
    {
      sys: { id: '5', type: 'Entry', createdAt: '2023-02-28', updatedAt: '2023-02-28', contentType: { sys: { id: 'blogPost' } } },
      fields: {
        title: 'Tracking Personal Progress Better',
        slug: 'tracking-personal-progress-better',
        excerpt: 'How do you measure your growth? What metrics do you trust? What tools do you use to help record and display those metrics?',
        description: 'How do you measure your growth? What metrics do you trust? What tools do you use to help record and display those metrics?',
        publishDate: '2023-02-28',
        author: 'Chris Smith',
        tags: ['Productivity']
      }
    },
    {
      sys: { id: '6', type: 'Entry', createdAt: '2023-01-26', updatedAt: '2023-01-26', contentType: { sys: { id: 'blogPost' } } },
      fields: {
        title: 'Story Mapping: An Introduction',
        slug: 'story-mapping-an-introduction',
        excerpt: 'Visual learning and storytelling have existed longer than spoken languages have been in existence.',
        description: 'Visual learning and storytelling have existed longer than spoken languages have been in existence.',
        publishDate: '2023-01-26',
        author: 'Chris Smith',
        tags: ['Productivity', 'Developer Skills']
      }
    }
  ];

  const displayPosts = posts.length > 0 ? posts : mockPosts;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-12 text-center">
        <h1 className="text-5xl font-bold text-gray-900 mb-4">Articles and Guides</h1>
        <p className="text-xl text-gray-600">Interesting Thoughts</p>
        {selectedCategory && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="text-sm text-gray-600">Filtered by:</span>
            <span className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full font-medium">
              {selectedCategory}
              <button
                onClick={() => setSearchParams({})}
                className="hover:text-green-900"
                aria-label="Clear filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Masonry Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-auto">
        {displayPosts.map((post) => (
          <Link
            key={post.sys.id}
            to={`/blog/${post.fields.slug}`}
            className="group block"
          >
            <article className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 h-full flex flex-col">
              {/* Featured Image - prioritize heroImage, then bannerImage, then featuredImage */}
              {(post.fields.heroImage?.[0] || post.fields.bannerImage?.[0] || post.fields.featuredImage) ? (
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={
                      post.fields.heroImage?.[0]?.secure_url ||
                      post.fields.bannerImage?.[0]?.secure_url ||
                      (post.fields.featuredImage?.fields.file.url.startsWith('//') 
                        ? `https:${post.fields.featuredImage.fields.file.url}`
                        : post.fields.featuredImage?.fields.file.url)
                    }
                    alt={post.fields.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                  <div className="text-white text-center p-6">
                    <svg className="w-16 h-16 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                {/* Tags */}
                {post.fields.tags && post.fields.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {post.fields.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs font-semibold uppercase tracking-wide text-green-600 bg-green-50 px-3 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Title */}
                <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors line-clamp-2">
                  {post.fields.title}
                </h2>

                {/* Meta */}
                <div className="text-sm text-gray-500 mb-4 flex items-center gap-2">
                  <time dateTime={post.fields.publishDate}>
                    {formatDate(post.fields.publishDate)}
                  </time>
                  {post.fields.author && (
                    <>
                      <span>•</span>
                      <span>by {post.fields.author}</span>
                    </>
                  )}
                </div>

                {/* Excerpt or Description */}
                {(post.fields.excerpt || post.fields.description) && (
                  <p className="text-gray-600 leading-relaxed line-clamp-3 flex-1">
                    {post.fields.excerpt || post.fields.description}
                  </p>
                )}

                {/* Read More Link */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <span className="text-green-600 font-medium group-hover:text-green-700 inline-flex items-center">
                    Read more
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </article>
          </Link>
        ))}
      </div>

      {/* Sidebar-like section at bottom on mobile, sidebar on desktop */}
      <aside className="mt-16 grid md:grid-cols-3 gap-8 bg-gray-50 p-8 rounded-lg">
        {/* Categories */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Categories</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSearchParams({})}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                !selectedCategory 
                  ? 'bg-green-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-green-600 hover:text-green-600'
              }`}
            >
              <span>All Articles</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                !selectedCategory
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {allPosts.length}
              </span>
            </button>
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSearchParams({ category: category.name })}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                  selectedCategory === category.name
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-green-600 hover:text-green-600'
                }`}
              >
                <span>{category.name}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  selectedCategory === category.name
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {category.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Posts */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Posts</h3>
          <ul className="space-y-3">
            {displayPosts.slice(0, 5).map((post) => (
              <li key={post.sys.id}>
                <Link
                  to={`/blog/${post.fields.slug}`}
                  className="text-gray-700 hover:text-green-600 transition-colors text-sm"
                >
                  {post.fields.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Archives */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Archives</h3>
          <div className="flex flex-wrap gap-2">
            {archives.map((archive) => (
              <button
                key={`${archive.year}-${archive.month}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all bg-white text-gray-700 border border-gray-200 hover:border-green-600 hover:text-green-600"
              >
                <span>{archive.label}</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                  {archive.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Blog;
